// Role-aware signup. ?role=customer (default) | merchant.
// Recipient self-signup uses phone OTP — lands in Phase 1.2 (Twilio).
//
// Form fields by role:
//   customer: full_name, email, password, country (ISO-2)
//   merchant: full_name, email, password, business_name, city_id (from catalog)
//
// On success the server creates the User + role profile + opens a
// Sanctum session. We refresh AuthContext and route to the right
// dashboard.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail, Lock, User as UserIcon, Globe, Store, MapPin, ShoppingBag, LocateFixed, Check, Home, Phone, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, auth, catalog, listings } from '../../lib/api';
import { suggestPassword, scoreStrength } from '../../lib/password';
import { useAuth } from '../../contexts/AuthContext';

type Role = 'customer' | 'merchant';

const COUNTRIES: Array<{ value: string; label: string }> = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'FR', label: 'France' },
  { value: 'HT', label: 'Haiti' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'CL', label: 'Chile' },
  { value: 'BR', label: 'Brazil' },
  { value: 'OT', label: 'Other' },
];

export function Register() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleParam = (params.get('role') === 'merchant' ? 'merchant' : 'customer') as Role;
  const phoneParam = params.get('phone') || '';
  const returnTo = params.get('returnTo');

  const [role, setRole] = useState<Role>(roleParam);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const pwStrength = useMemo(() => scoreStrength(password), [password]);
  const [country, setCountry] = useState('US');
  const [businessName, setBusinessName] = useState('');
  const [cityId, setCityId] = useState<number | ''>('');
  const [addressLine, setAddressLine] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Merchant location — pinned via browser geolocation so we can route
  // recipients to the nearest store. Editable as a manual fallback.
  const [lat, setLat] = useState<number | ''>('');
  const [lng, setLng] = useState<number | ''>('');
  const [locating, setLocating] = useState(false);
  const [locAccuracy, setLocAccuracy] = useState<number | null>(null);

  const captureLocation = () => {
    if (!('geolocation' in navigator)) { toast.error('Geolocation not supported on this device.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(Number(pos.coords.latitude.toFixed(7)));
        setLng(Number(pos.coords.longitude.toFixed(7)));
        setLocAccuracy(pos.coords.accuracy ? Math.round(pos.coords.accuracy) : null);
        setLocating(false);
        toast.success('Location captured');
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === err.PERMISSION_DENIED
          ? 'Location permission denied — you can type coordinates manually.'
          : 'Could not get your location — try again or enter it manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // Re-sync role if URL param changes (e.g. user toggles the segmented control).
  useEffect(() => { setRole(roleParam); }, [roleParam]);

  // Cities for merchant city picker; only needed when role=merchant.
  const citiesQ = useQuery({
    queryKey: ['catalog', 'cities'],
    queryFn: catalog.cities,
    enabled: role === 'merchant',
  });

  // Claiming a pre-populated OSM listing → prefill the merchant form from it.
  const listingId = params.get('listing');
  const listingQ = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => listings.get(Number(listingId)),
    enabled: !!listingId,
  });
  useEffect(() => {
    const l = listingQ.data;
    if (!l) return;
    setRole('merchant');
    setBusinessName((v) => v || l.name);
    setAddressLine((v) => v || l.address || '');
    setPhone((v) => v || l.phone || '');
    setCityId((v) => (v === '' && l.city_id ? l.city_id : v));
    setLat((v) => (v === '' ? Number(l.latitude) : v));
    setLng((v) => (v === '' ? Number(l.longitude) : v));
  }, [listingQ.data]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = role === 'customer'
        ? await auth.register({
            role: 'customer',
            email: email.trim(),
            password,
            full_name: fullName.trim(),
            country,
          })
        : await auth.register({
            role: 'merchant',
            email: email.trim(),
            password,
            full_name: fullName.trim(),
            business_name: businessName.trim(),
            city_id: cityId === '' ? 0 : cityId,
            address_line: addressLine.trim() || null,
            phone: phone.trim() || null,
            latitude: lat === '' ? null : lat,
            longitude: lng === '' ? null : lng,
            listing_id: listingId ? Number(listingId) : null,
          });
      await refresh();
      toast.success('Account created');
      // Customers who came from a care box (returnTo) go back to it; the
      // ?send=1 on that URL re-opens the send modal. Merchants ignore it.
      const home = user.role === 'merchant' ? '/merchant' : '/customer';
      navigate(user.role === 'customer' && returnTo ? returnTo : home, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Signup failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-panel)] flex flex-col">
      <Header />
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-lg bg-white rounded-2xl border border-[var(--color-border)] shadow-sm p-7">
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--color-ink)]">Create your VoyeLakay account</h1>
          <p className="mt-1 text-[13px] text-[var(--color-secondary-2)]">
            {role === 'customer'
              ? 'Support family in Haiti with verified purchases.'
              : 'Become an approved fulfillment merchant.'}
          </p>

          {/* Role toggle */}
          <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-[var(--color-panel)] rounded-lg">
            <RoleChip active={role === 'customer'} onClick={() => navigate(`/register?role=customer${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`, { replace: true })} label="I'm diaspora" sub="Buy for family" />
            <RoleChip active={role === 'merchant'} onClick={() => navigate(`/register?role=merchant${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`, { replace: true })} label="I'm a merchant" sub="Fulfill orders in HT" />
          </div>

          {listingQ.data && (
            <div className="mt-4 rounded-lg bg-[var(--color-success-bg)] border border-[var(--color-success-border)] p-3">
              <p className="text-[12.5px] font-semibold text-[var(--color-success-fg)] inline-flex items-center gap-1.5"><Store className="w-4 h-4" /> Claiming “{listingQ.data.name}”</p>
              <p className="text-[11.5px] text-[var(--color-success-fg)] mt-0.5">{listingQ.data.address || listingQ.data.city?.name || 'Haiti'} · we've pre-filled your details below — adjust anything, then create your account to submit for KYB review.</p>
            </div>
          )}

          {role === 'merchant' && !listingId && (
            <p className="mt-3 text-[12px] text-[var(--color-secondary-2)]">
              Already operating a store?{' '}
              <Link to="/claim" className="text-brand-600 font-semibold hover:underline">Find &amp; claim your listing</Link> — your details get pre-filled.
            </p>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <Field icon={UserIcon} label={role === 'merchant' ? 'Your name (authorized representative)' : 'Full name'} type="text" value={fullName} onChange={setFullName} autoComplete="name" required />
            <Field icon={Mail}     label="Email"     type="email" value={email} onChange={setEmail} autoComplete="email" required />
            <Field icon={Lock}     label="Password"  type="password" value={password} onChange={setPassword} autoComplete="new-password" required hint="Minimum 8 characters." />
            <div className="-mt-2 flex items-center gap-2">
              {password ? (
                <>
                  <div className="flex-1 h-1.5 rounded-full bg-[var(--color-panel)] overflow-hidden">
                    <div className={`h-full ${pwStrength.cls} transition-all`} style={{ width: `${(pwStrength.score / 4) * 100}%` }} />
                  </div>
                  <span className="text-[10.5px] font-semibold text-[var(--color-secondary-2)] w-14 text-right">{pwStrength.label}</span>
                </>
              ) : <span className="flex-1" />}
              <button type="button" onClick={() => { const pw = suggestPassword(16); setPassword(pw); navigator.clipboard?.writeText(pw).then(() => toast.success('Strong password generated + copied'), () => {}); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:underline shrink-0">
                <Sparkles className="w-3 h-3" /> Suggest strong
              </button>
            </div>

            {role === 'customer' && (
              <Select icon={Globe} label="Country" value={country} onChange={setCountry}>
                {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            )}

            {role === 'merchant' && (
              <>
                <Field icon={Store} label="Business name" type="text" value={businessName} onChange={setBusinessName} required />
                <Field icon={Home} label="Business address" type="text" value={addressLine} onChange={setAddressLine} hint="Street, neighborhood — printed on your merchant certificate." />
                <Field icon={Phone} label="Business phone" type="tel" value={phone} onChange={setPhone} autoComplete="tel" />
                <Select icon={MapPin} label="City (Haiti)" value={cityId === '' ? '' : String(cityId)} onChange={(v) => setCityId(v === '' ? '' : Number(v))} required>
                  <option value="">Select a city…</option>
                  {(citiesQ.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>

                {/* Pin exact store location for nearest-merchant routing */}
                <div className="rounded-lg border border-[var(--color-border)] p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1">
                      <LocateFixed className="w-3.5 h-3.5" /> Store location
                    </span>
                    {lat !== '' && lng !== '' && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--color-success-fg)]"><Check className="w-3 h-3" /> Pinned</span>
                    )}
                  </div>
                  <p className="mt-1 text-[11.5px] text-[var(--color-secondary-2)]">Stand at your store and pin it — we route nearby recipients to the closest merchant.</p>
                  <button
                    type="button"
                    onClick={captureLocation}
                    disabled={locating}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-ink)] text-white text-[12px] font-semibold hover:bg-[var(--color-primary)] disabled:opacity-60"
                  >
                    {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />}
                    {locating ? 'Locating…' : lat === '' ? 'Use my current location' : 'Update location'}
                  </button>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-muted)]">Latitude</span>
                      <input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value === '' ? '' : Number(e.target.value))} placeholder="18.51" className="mt-0.5 w-full px-2 py-1.5 text-[12.5px] font-mono border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-muted)]">Longitude</span>
                      <input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value === '' ? '' : Number(e.target.value))} placeholder="-72.63" className="mt-0.5 w-full px-2 py-1.5 text-[12.5px] font-mono border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                    </label>
                  </div>
                  {locAccuracy != null && <p className="mt-1.5 text-[10.5px] text-[var(--color-muted)]">Accuracy ≈ {locAccuracy} m{locAccuracy > 100 ? ' · move outdoors for a sharper pin' : ''}</p>}
                </div>

                <p className="text-[11.5px] text-[var(--color-secondary-2)] -mt-1">Merchant accounts require KYB review before fulfilling orders.</p>
              </>
            )}

            {phoneParam && (
              <p className="text-[11.5px] text-[var(--color-secondary-2)] bg-[var(--color-warning-bg)] border border-[var(--color-warning-border)] rounded-md px-3 py-2">
                We'll connect this account to your family member at <strong className="text-[var(--color-ink)]">{phoneParam}</strong> after signup.
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="mt-5 text-[12.5px] text-[var(--color-secondary-2)] text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 font-semibold hover:underline">Sign in</Link>
          </p>
        </div>

        <p className="mt-6 text-[11px] text-[var(--color-muted)]">
          By creating an account you agree to our <Link to="/terms" className="hover:text-[var(--color-body)]">Terms</Link> and <Link to="/privacy" className="hover:text-[var(--color-body)]">Privacy notice</Link>.
        </p>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-[var(--color-hairline)]">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
            <ShoppingBag className="w-4 h-4" />
          </span>
          <span className="font-semibold tracking-tight">VoyeLakay</span>
        </Link>
        <Link to="/" className="text-[13px] text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]">← Back</Link>
      </div>
    </header>
  );
}

function RoleChip({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left px-3 py-2 rounded-md transition-colors ${active ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
    >
      <p className={`text-[13px] font-semibold ${active ? 'text-[var(--color-ink)]' : 'text-[var(--color-secondary)]'}`}>{label}</p>
      <p className="text-[11px] text-[var(--color-secondary-2)]">{sub}</p>
    </button>
  );
}

function Field({
  icon: Icon, label, type, value, onChange, autoComplete, required, hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</span>
      <div className="relative mt-1 flex items-center bg-white rounded-lg border border-[var(--color-border)] focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400">
        <Icon className="absolute left-3 w-4 h-4 text-[var(--color-muted)]" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          className="w-full pl-9 pr-3 py-2.5 text-[14px] bg-transparent rounded-lg focus:outline-none"
        />
      </div>
      {hint && <span className="block mt-1 text-[11px] text-[var(--color-muted)]">{hint}</span>}
    </label>
  );
}

function Select({
  icon: Icon, label, value, onChange, required, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</span>
      <div className="relative mt-1 flex items-center bg-white rounded-lg border border-[var(--color-border)] focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400">
        <Icon className="absolute left-3 w-4 h-4 text-[var(--color-muted)] pointer-events-none" />
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full pl-9 pr-3 py-2.5 text-[14px] bg-transparent rounded-lg focus:outline-none appearance-none"
        >
          {children}
        </select>
      </div>
    </label>
  );
}
